import React from 'react';
import { useIntl } from 'react-intl';
import Button from '../../../../../ui/canon/Button';
import MarkdownMessage from '../../../assistant/components/MarkdownMessage';
import { SparklesIcon, CheckIcon, XIcon, Loader2Icon } from 'lucide-react';

const AiSuggestionCard = ({
  suggestion,
  reasoning,
  confidence,
  isLoading,
  error,
  onUseSuggestion,
  onDismiss,
  disabled,
}) => {
  const intl = useIntl();

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-2">
        <div className="flex items-start gap-3">
          <XIcon className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-700">
              {intl.formatMessage({ id: 'succession.plans.ai_suggestion_error' })}
            </p>
            <p className="text-sm text-red-600">{error}</p>
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={onDismiss}
          >
            {intl.formatMessage({ id: 'succession.plans.ai_suggestion_dismiss' })}
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="bg-succession-primary/5 border border-succession-primary/20 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <Loader2Icon className="h-4 w-4 text-succession-primary animate-spin" />
          <div className="flex-1">
            <p className="text-sm font-medium text-succession-text-primary">
              {intl.formatMessage({ id: 'succession.plans.ai_suggestion_generating' })}
            </p>
            <p className="text-sm text-succession-text-secondary">
              {intl.formatMessage({ id: 'succession.plans.ai_suggestion_analyzing' })}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!suggestion) {
    return null;
  }

  // Format confidence as percentage
  const confidencePercent = confidence ? Math.round(confidence * 100) : null;
  const confidenceLabel = confidencePercent
    ? confidencePercent >= 80
      ? intl.formatMessage({ id: 'succession.plans.ai_suggestion_high_confidence' })
      : confidencePercent >= 60
        ? intl.formatMessage({ id: 'succession.plans.ai_suggestion_moderate_confidence' })
        : intl.formatMessage({ id: 'succession.plans.ai_suggestion_low_confidence' })
    : null;

  return (
    <div className="bg-succession-primary/5 border border-succession-primary/20 rounded-lg p-4 space-y-3 transition-all duration-300 animate-in fade-in slide-in-from-top-2">
      <div className="flex items-start gap-3">
        <SparklesIcon className="h-4 w-4 text-succession-primary flex-shrink-0 mt-0.5" />
        <div className="flex-1 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-succession-text-primary">
              {intl.formatMessage({ id: 'succession.plans.ai_suggestion_label' })}
            </p>
            {confidenceLabel && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                confidencePercent >= 80
                  ? 'bg-green-100 text-green-700'
                  : confidencePercent >= 60
                    ? 'bg-yellow-100 text-yellow-700'
                    : 'bg-gray-100 text-gray-600'
              }`}>
                {confidenceLabel}
              </span>
            )}
          </div>

          <div className="bg-white/50 rounded-md p-3 border border-succession-border">
            <div className="chat_result" dangerouslySetInnerHTML={{ __html: suggestion }} />
          </div>
        </div>
      </div>

      <div className="flex gap-2 justify-end">
        <Button
          variant="ghost"
          size="sm"
          onClick={onDismiss}
          disabled={disabled}
        >
          <XIcon className="h-3.5 w-3.5 mr-1" />
          {intl.formatMessage({ id: 'succession.plans.ai_suggestion_dismiss' })}
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => onUseSuggestion(suggestion)}
          disabled={disabled}
          className="gap-2"
        >
          <CheckIcon className="h-3.5 w-3.5" />
          {intl.formatMessage({ id: 'succession.plans.ai_suggestion_use' })}
        </Button>
      </div>
    </div>
  );
};

export default AiSuggestionCard;
